/**
 * SearchResultItem - Unified premium component for search results
 * 
 * Used for:
 * - Autocomplete suggestions
 * - Search results
 * - Category search results
 * 
 * Premium styling with consistent hierarchy and branding
 */

import { useState } from 'react';
import { Copy, Check, Navigation, Footprints, Bike, Car, Ruler, Clock } from 'lucide-react';
import { DynamicIcon } from '../ui/DynamicIcon';
import { formatPostcodeFull } from '../../utils/postcode';
import { useSettingsStore } from '../../stores/settingsStore';
import { formatDistance as fmtDist } from '../../services/mapbox';
import { CloudRain } from 'lucide-react';
import { floodRiskBadgeClass, floodRiskLabel } from '../../lib/floodExposure';

function travelTime(meters: number, speedMs: number): string {
  const secs = meters / speedMs;
  if (secs < 60) return `${Math.round(secs)}s`;
  const mins = secs / 60;
  if (mins < 60) return `${Math.round(mins)} min`;
  const hrs = mins / 60;
  const h = Math.floor(hrs);
  const m = Math.round((hrs - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

const walkingTime = (m: number) => travelTime(m, 1.4);   // ~5 km/h
const bikingTime = (m: number) => travelTime(m, 4.2);   // ~15 km/h
const drivingTime = (m: number) => travelTime(m, 8.3);   // ~30 km/h urban

function hoursBadgeLabel(status: string): string {
  if (status === 'Open 24/7') return 'Open 24/7';
  // Keep Open/Closed prefix so status is obvious at a glance
  return status;
}

/** Compact hours label for recent-search subtitles. */
export function formatHoursForSubtitle(status: string | null | undefined): string | null {
  if (!status) return null;
  return hoursBadgeLabel(status);
}

function hoursBadgeTone(status: string): 'open' | 'closed' | 'neutral' {
  if (status.startsWith('Open')) return 'open';
  if (status.startsWith('Closed')) return 'closed';
  return 'neutral';
}

const hoursBadgeClass: Record<ReturnType<typeof hoursBadgeTone>, string> = {
  open: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/30',
  closed: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30',
  neutral: 'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700/60',
};

/** Format raw Coordal codes in titles (KLA0CB1473 → KLA0C B1473). */
export function formatSearchResultName(name: string): string {
  const compact = name.replace(/\s+/g, '').toUpperCase();
  if (/^[A-Z]{2}[A-Z0-9]*B\d/.test(compact)) {
    return formatPostcodeFull(compact) || name;
  }
  return name;
}

interface SearchResultItemProps {
  name: string;
  address?: string | null;
  district?: string | null;
  categoryLabel?: string;
  coordalPostcode?: string | null;
  coordalDisplay?: string | null;
  ghanaPostcode?: string | null;
  openingHoursStatus?: string | null;
  distance?: string | null;
  distanceMeters?: number | null;
  iconName?: string;
  onClick: () => void;
  onDirectionsClick?: () => void;
  showDivider?: boolean;
  compact?: boolean;
  isPlace?: boolean;
  floodRiskLevel?: string | null;
  floodProne?: boolean | null;
}

/**
 * Premium search result item with consistent styling
 */
export function SearchResultItem({
  name,
  address,
  district,
  categoryLabel,
  coordalPostcode,
  coordalDisplay,
  ghanaPostcode,
  openingHoursStatus,
  distance,
  distanceMeters,
  iconName = 'map-pin',
  onClick,
  onDirectionsClick,
  showDivider = true,
  compact = false,
  isPlace = false,
  floodRiskLevel,
  floodProne,
}: SearchResultItemProps) {
  const [copied, setCopied] = useState(false);
  const { settings } = useSettingsStore();

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (coordalPostcode) {
      // Copy full postcode without spaces for clean paste
      const cleanCode = coordalPostcode.replace(/\s+/g, '');
      navigator.clipboard.writeText(cleanCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Use pre-formatted postcode from API (already has space after service area)
  const displayPostcode = coordalDisplay || coordalPostcode || '';
  const displayName = formatSearchResultName(name);

  const fullAddressLine = (() => {
    if (!address && !district) return null;
    if (!district) return address;
    if (!address) return district;
    if (address.toLowerCase().includes(district.toLowerCase())) return address;
    return [address, district].filter(Boolean).join(', ');
  })();

  const displayDistance = distance || (distanceMeters ? fmtDist(distanceMeters, settings.distanceUnit) : null);

  const handleDirectionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDirectionsClick?.();
  };

  return (
    <div
      className={`
        ${showDivider ? 'border-b border-gray-100/80 dark:border-gray-700/80 last:border-b-0' : ''}
        cursor-pointer
        hover:bg-gray-50 dark:hover:bg-gray-700/50 active:bg-gray-100/60 dark:active:bg-gray-700
        transition-colors duration-100
      `}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      <div className={`
        w-full flex items-start gap-2.5 text-left
        ${compact ? 'px-3.5 py-2.5' : 'px-3.5 py-3'}
      `}>
        {/* Left Icon */}
        <div className={`
          flex-shrink-0 rounded-lg flex items-center justify-center mt-0.5
          bg-emerald-50 dark:bg-emerald-900/30
          ${compact ? 'w-8 h-8' : 'w-9 h-9'}
        `}>
          <DynamicIcon
            name={iconName}
            size={compact ? 16 : 18}
            className="text-emerald-600 dark:text-emerald-400"
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Name + Category inline */}
          <div className="flex items-baseline gap-2 min-w-0">
            <p className={`
              font-semibold text-gray-900 dark:text-white truncate flex-1 min-w-0
              ${compact ? 'text-[13px]' : 'text-sm'}
            `}>
              {displayName}
            </p>
            {categoryLabel && (
              <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-md flex-shrink-0 whitespace-nowrap">
                {categoryLabel}
              </span>
            )}
            {floodRiskLevel && (
              <span
                className={`inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-md flex-shrink-0 whitespace-nowrap ${floodRiskBadgeClass(floodRiskLevel)}`}
                title={floodProne ? 'Often flood-prone during the rainy season' : 'Seasonal flood risk based on long-term rainfall patterns'}
              >
                <CloudRain size={10} className="flex-shrink-0" strokeWidth={2.25} />
                {floodRiskLabel(floodRiskLevel)}
              </span>
            )}
          </div>
          {/* Full Address — street + district/region combined */}
          {(fullAddressLine) && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
              {fullAddressLine}
            </p>
          )}

          {/* Postcodes row - compact inline badges */}
          {displayPostcode ? (
            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
              {/* Coordal Postcode */}
              <div className="flex items-center gap-1 bg-cyan-50 dark:bg-cyan-900/30 rounded-md px-1.5 py-0.5">
                <img src="/logos/coordal-logo.svg" alt="" className="w-3 h-3" />
                <span className="text-[11px] font-semibold text-cyan-700 dark:text-cyan-300">{displayPostcode}</span>
                <div
                  onClick={handleCopy}
                  className={`ml-0.5 cursor-pointer transition-colors ${copied ? 'text-emerald-600' : 'text-cyan-400 dark:text-cyan-500 hover:text-cyan-600'}`}
                  title={copied ? 'Copied!' : 'Copy'}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleCopy(e as unknown as React.MouseEvent)}
                >
                  {copied ? <Check size={10} /> : <Copy size={10} />}
                </div>
              </div>

              {/* GhanaPost GPS */}
              {ghanaPostcode ? (
                <div className="flex items-center gap-1 bg-gray-50 dark:bg-gray-700 rounded-md px-1.5 py-0.5">
                  <img src="/ghanapost-favicon.png" alt="" className="w-3 h-3" />
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">{ghanaPostcode}</span>
                </div>
              ) : (
                <div
                  className="flex items-center gap-1 text-[11px] text-orange-500 hover:text-orange-600 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    console.log('Add GhanaPost GPS code');
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <img src="/ghanapost-favicon.png" alt="" className="w-3 h-3 opacity-40" />
                  <span>+ GPS</span>
                </div>
              )}

            </div>
          ) : isPlace && district ? (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[11px] text-gray-400 dark:text-gray-500">{district}</span>
            </div>
          ) : null}

          {/* Hours + distance + travel times — unified pill row */}
          {(openingHoursStatus || displayDistance || distanceMeters != null) && (
            <div className="flex items-center gap-1 mt-1.5 flex-wrap">
              {openingHoursStatus && (
                <span
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap flex items-center gap-0.5 ${hoursBadgeClass[hoursBadgeTone(openingHoursStatus)]}`}
                >
                  <Clock size={9} className="flex-shrink-0 opacity-80" />
                  {hoursBadgeLabel(openingHoursStatus)}
                </span>
              )}
              {displayDistance && (
                <span className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-md whitespace-nowrap flex items-center gap-0.5">
                  <Ruler size={9} className="flex-shrink-0" />
                  {displayDistance}
                </span>
              )}
              {distanceMeters != null && (
                <span className="text-[10px] text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/60 px-1.5 py-0.5 rounded-md whitespace-nowrap flex items-center gap-0.5">
                  <Footprints size={9} className="flex-shrink-0" />
                  {walkingTime(distanceMeters)}
                </span>
              )}
              {distanceMeters != null && (
                <span className="text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded-md whitespace-nowrap flex items-center gap-0.5">
                  <Bike size={9} className="flex-shrink-0" />
                  {bikingTime(distanceMeters)}
                </span>
              )}
              {distanceMeters != null && (
                <span className="text-[10px] text-orange-500 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30 px-1.5 py-0.5 rounded-md whitespace-nowrap flex items-center gap-0.5">
                  <Car size={9} className="flex-shrink-0" />
                  {drivingTime(distanceMeters)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Directions button */}
        {onDirectionsClick && (
          <div
            onClick={handleDirectionsClick}
            className="flex-shrink-0 mt-1 p-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 cursor-pointer transition-colors"
            title="Get directions"
            role="button"
            tabIndex={0}
          >
            <Navigation size={14} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * SearchResultsList - Container for search results with proper styling
 */
interface SearchResultsListProps {
  children: React.ReactNode;
  className?: string;
}

export function SearchResultsList({ children, className = '' }: SearchResultsListProps) {
  return (
    <div className={`
      bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900/50 overflow-hidden
      ${className}
    `}>
      {children}
    </div>
  );
}

/**
 * SearchResultsHeader - Section header for result groups
 */
interface SearchResultsHeaderProps {
  title: string;
  count?: number;
}

export function SearchResultsHeader({ title, count }: SearchResultsHeaderProps) {
  return (
    <div className="px-4 py-2.5 bg-gray-50/80 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        {title}
        {count !== undefined && (
          <span className="ml-1.5 text-gray-400 dark:text-gray-500">({count})</span>
        )}
      </p>
    </div>
  );
}

/**
 * EmptySearchResults - Shown when no results found
 */
interface EmptySearchResultsProps {
  message?: string;
  suggestion?: string;
  onAddMissingPlace?: () => void;
}

export function EmptySearchResults({
  message = 'No results found',
  suggestion = 'Try a different search term',
  onAddMissingPlace,
}: EmptySearchResultsProps) {
  return (
    <div className="p-8 text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
        <DynamicIcon name="map-pin" size={24} className="text-gray-400 dark:text-gray-500" />
      </div>
      <p className="text-gray-600 dark:text-gray-300 font-medium">{message}</p>
      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">{suggestion}</p>
      {onAddMissingPlace && (
        <button
          onClick={onAddMissingPlace}
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm font-medium text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
        >
          <DynamicIcon name="map-pin-plus" size={16} className="text-emerald-500" />
          Add this place
        </button>
      )}
    </div>
  );
}

/**
 * SearchLoading - Loading state for search
 */
export function SearchLoading() {
  return (
    <div className="p-8 text-center">
      <div className="w-8 h-8 mx-auto mb-3 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-gray-500">Searching...</p>
    </div>
  );
}

/**
 * SearchResultsHelper - Helper text at end of results to improve search
 */
interface SearchResultsHelperProps {
  query?: string;
}

export function SearchResultsHelper({ query }: SearchResultsHelperProps) {
  return (
    <div className="px-4 py-3 bg-gray-50/50 dark:bg-gray-700/30 border-t border-gray-100 dark:border-gray-700">
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        Can't find what you're looking for?{' '}
        <span className="text-emerald-600 dark:text-emerald-400">
          Try being more specific
        </span>
        {query && (
          <span className="text-gray-400 dark:text-gray-500">
            {' '}or search by address, postcode, or category
          </span>
        )}
      </p>
    </div>
  );
}
